import { useState } from "react";

import type { LibraryShow } from "@/api/schemas";
import { BUCKET_LABELS, bucketOf, progress } from "@/lib/grouping";
import type { Bucket } from "@/lib/grouping";
import { posterUrl } from "@/lib/poster";
import type { ShowDetail } from "@/store/details";

const BUCKETS: Bucket[] = ["watching", "uptodate", "completed"];

const EMPTY_HINTS: Record<Bucket, string> = {
  watching: "Search for a show to start tracking it.",
  uptodate: "Shows land here once you catch up on one that is still running.",
  completed: "Shows land here once you finish one that has ended.",
};

function Cell({ show, bucket, onOpen }: { show: LibraryShow; bucket: Bucket; onOpen: () => void }) {
  const poster = posterUrl(show.show.poster, "grid");
  // Every cell carries a bar, and its colour is the bucket: yellow while
  // there is something left, green once caught up on a show still running,
  // purple once the show itself has ended. A caught-up show fills the bar
  // whatever the unaired count says, because there is nothing left to watch.
  const caughtUp = bucket !== "watching";
  const fraction = caughtUp ? 1 : progress(show);
  return (
    <button type="button" className="cell" onClick={onOpen}>
      <span className="cell-art">
        {poster !== null && <img src={poster} alt="" loading="lazy" decoding="async" />}
        <span className={`cell-bar ${bucket}`}>
          <span style={{ transform: `scaleX(${String(fraction)})`, transformOrigin: "left" }} />
        </span>
      </span>
      <span className="cell-name">{show.show.title}</span>
      <span className="cell-sub sub">
        {show.watchedEpisodes} of {show.totalEpisodes}
      </span>
    </button>
  );
}

/** Loose enough that "punisher" finds "Marvel's The Punisher". */
function matches(title: string, query: string): boolean {
  return title.toLowerCase().includes(query.trim().toLowerCase());
}

export function Library({
  shows,
  loading,
  details,
  query,
  onOpen,
}: {
  shows: readonly LibraryShow[];
  loading: boolean;
  details: ReadonlyMap<number, ShowDetail>;
  /** Null when the header's search field is closed. */
  query: string | null;
  onOpen: (show: LibraryShow) => void;
}) {
  const [bucket, setBucket] = useState<Bucket>("watching");

  if (loading && shows.length === 0) {
    return (
      <div className="grid">
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <span key={n} className="cell">
            <span className="cell-art" />
          </span>
        ))}
      </div>
    );
  }

  // Searching looks across the whole library, not the selected tab. Hunting
  // for a show you cannot find is exactly the case where you do not know
  // which bucket it landed in.
  const searching = query !== null && query.trim().length > 0;
  const visible = shows
    .filter((show) =>
      searching
        ? matches(show.show.title, query)
        : bucketOf(show, details.get(show.show.ids.simkl)?.airing) === bucket,
    )
    .toSorted((a, b) => a.show.title.localeCompare(b.show.title));

  return (
    <>
      {!searching && (
        <div className="segmented" role="tablist">
          {BUCKETS.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={option === bucket}
              onClick={() => {
                setBucket(option);
              }}
            >
              {BUCKET_LABELS[option]}
            </button>
          ))}
        </div>
      )}
      {visible.length === 0 ? (
        <div className="empty">
          <strong>{searching ? "No match" : `Nothing in ${BUCKET_LABELS[bucket]}`}</strong>
          <p>{searching ? "No show in your library has that in its name." : EMPTY_HINTS[bucket]}</p>
        </div>
      ) : (
        <div className="grid">
          {visible.map((show) => (
            <Cell
              key={show.show.ids.simkl}
              show={show}
              bucket={bucketOf(show, details.get(show.show.ids.simkl)?.airing)}
              onOpen={() => {
                onOpen(show);
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}
